import type { GetServerSidePropsContext } from 'next';

import type { HeadersContext } from '@/data/appApi';
import { calmLogger } from '@/utils/calmLogger';
import { getBrowserLanguageFromHeaders } from '@/utils/getBrowserLanguageFromHeaders';
import { getCalmLanguage } from '@/utils/language';

/**
 * Options threaded into a migrated server-side init fetch (`getPartner`,
 * `getFeatureFlags`, …) to route it straight to internal app-api instead of the
 * public `www.calm.com/webapi/authproxy` edge. `ctx` carries the Pages-Router
 * `req`/`res` so `getHeaders` uses its Pages branch.
 */
export type PagesRouterSsrDirectOpts = { ctx: HeadersContext; xLanguage?: string };

const firstHeaderValue = (value: string | string[] | undefined): string | undefined =>
	Array.isArray(value) ? value[0] : value;

/**
 * Pages-Router mirror of the gate evaluation `loadInitialState` does for the
 * App Router. Evaluates the `www-ssr-app-api-direct` kill-switch once
 * for this request and, when ON, returns the `{ ctx, xLanguage }` to thread into the
 * migrated init fetches so they bypass the WAF-blocked authproxy edge. Returns
 * `undefined` when OFF, or on ANY failure resolving the gate — callers then stay on
 * the authproxy. That guarantee lives here (not at each call site) so it covers every
 * caller, including the ones that predate this fail-safe wrapping.
 *
 * The Amplitude gate is reached via a lazy `import()` of `ssrAppApiDirectGate.impl`
 * (not the `server-only` `ssrAppApiDirectGate` entry): the Pages Router forbids a
 * `server-only` marker anywhere in a page's import graph, and the dynamic import
 * also keeps the Amplitude node SDK out of the Pages client bundle — the same idiom
 * `getFeatureFlags`/`appApi` use for their server-only deps. Only ever invoked from
 * `getServerSideProps` (server-side), so the runtime is always Node.
 */
export async function getPagesRouterSsrDirectOpts(
	context: GetServerSidePropsContext,
): Promise<PagesRouterSsrDirectOpts | undefined> {
	const { req, res } = context;

	try {
		// Bucket on the same device id `getHeaders` will send as `X-Device-Id`: it's on the
		// cookie once set, but on the header for a first-visit gSSP request (the common case
		// for these landing pages). Mirrors getHeaders' Pages branch + the App-Router
		// reference; `||` (not `??`) so an empty-string cookie also falls through to the header.
		const deviceId = req.cookies['x-device-id'] || firstHeaderValue(req.headers['x-device-id']);

		// Derive the locale from Accept-Language, NOT `context.locale`: apps/www has no Next
		// i18n config, so `context.locale` is always undefined for Pages-Router gSSP. This mirrors
		// getServerSideProps' own `detectedLocale` and the App-Router gate
		// (getCalmLanguage ∘ getBrowserLanguageFromHeaders), giving both the gate's `language`
		// bucketing input and the threaded `x-language` a real locale. Without it the direct path
		// would send zero locale signal (buildServerHeaders doesn't forward Accept-Language),
		// a regression vs the authproxy path which forwarded Accept-Language.
		const language = getCalmLanguage(getBrowserLanguageFromHeaders(req.headers));

		const { isSsrAppApiDirectEnabled } = await import('./ssrAppApiDirectGate.impl');

		return isSsrAppApiDirectEnabled({
			device_id: deviceId || undefined,
			platform: 'web',
			language,
		})
			? { ctx: { router: 'pages', req, res }, xLanguage: language }
			: undefined;
	} catch (err) {
		// `isSsrAppApiDirectEnabled` already catches its own evaluation errors, but the
		// dynamic `import()` above (and the synchronous cookie/header reads before it) are
		// not covered by that guard — none of the ~90 Pages-Router callers wrap this call,
		// so an uncaught throw here would 500 the page's SSR instead of degrading to the
		// authproxy.
		calmLogger.error('getPagesRouterSsrDirectOpts failed; falling back to authproxy (OFF)', {}, err);
		return undefined;
	}
}
